import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mail, Bot, ShieldCheck, ArrowRight, Sparkles, MapPin, TrendingUp, CheckCircle2 } from "lucide-react";

const PILLARS = [
  {
    icon: Mail,
    kicker: "Pillar 01 · Physical Marketing",
    title: "Dominate a local ZIP code",
    body: "Premium 9×12 co-op direct mail that lands in 10,000 neighborhood homes. Shared postcard space keeps costs low while a trusted local brand puts your offer in every mailbox, with QR coupons that track real redemptions.",
    points: ["10,000 homes per drop", "Premium, standard & basic ad spaces", "QR-coupon redemption tracking"],
    to: "/postcards", cta: "Explore postcard mailers",
  },
  {
    icon: Bot,
    kicker: "Pillar 02 · Digital Automation",
    title: "Automate your digital storefront",
    body: "An enterprise-grade CRM sub-account, AI-ready directory listing, smart forms, booking calendar, and lead routing, the systems that capture every lead and follow up automatically, so attention turns into booked, paying customers.",
    points: ["AI & GEO search-ready listings", "CRM sub-account with smart forms", "Automated lead capture & follow-up"],
    to: "/grow-my-business", cta: "Grow my business",
  },
  {
    icon: ShieldCheck,
    kicker: "Pillar 03 · Client & Staff Portal",
    title: "A secure portal your customers & team love",
    body: "A beautiful, fully branded online experience for everyone you work with: secure client portals and staff logins with role-based key access, plus file sharing, invoices, appointments and messaging, all under the Saving America Money brand. One login, zero friction, enterprise-grade security.",
    points: ["Branded secure client & staff portals", "Role-based key access & permissions", "Files, invoices, bookings & messaging"],
    to: "/grow-my-business", cta: "Get set up",
  },
];

export default function About() {
  useEffect(() => { document.title = "About · Saving America Money"; }, []);
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-sam-navy text-sam-cream sam-grain">
        <div className="absolute inset-0 sam-grain opacity-40" />
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-sam-crimson/20 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 py-20 sm:py-24 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-widest backdrop-blur">
            <Sparkles className="h-4 w-4 text-sam-amber" />Who we are
          </span>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            We build the systems that let people and businesses <span className="text-sam-crimson">be wealthy.</span>
          </h1>
          <p className="mt-6 text-lg text-sam-cream/75 leading-relaxed max-w-3xl mx-auto">
            <span className="font-bold text-sam-cream">Saving America Money</span> is a premier growth agency dedicated to empowering independent businesses, local economies, and ambitious agency owners. We bridge the gap between high-impact physical marketing and enterprise-level digital automation, a seamless, start-to-finish infrastructure that removes operational friction and drives measurable revenue.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button asChild className="bg-sam-amber text-sam-navy hover:bg-sam-amber/90 font-semibold h-11 px-5" data-testid="about-plans-btn">
              <Link to="/mailer"><Mail className="h-4 w-4 mr-2" />Advertise on the mailer</Link>
            </Button>
            <Button asChild variant="outline" className="border-sam-cream/30 bg-white/5 text-sam-cream hover:bg-white/10 hover:text-sam-cream font-semibold h-11 px-5" data-testid="about-grow-btn">
              <Link to="/grow-my-business"><TrendingUp className="h-4 w-4 mr-2" />Grow my business</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-black text-sam-navy">Three pillars, one infrastructure</h2>
          <p className="text-sam-slate mt-2 max-w-2xl mx-auto">From dominating a local ZIP code with premium direct mail, to automating your digital storefront, to a secure branded portal that elevates the experience for your customers and staff.</p>
        </div>
        <div className="space-y-6">
          {PILLARS.map((p, i) => (
            <div key={p.title} data-testid={`pillar-${i + 1}`}
              className={`grid md:grid-cols-[auto_1fr_auto] items-center gap-6 rounded-3xl border border-border bg-card p-6 sm:p-8 ${i % 2 ? "md:bg-sam-navy/[0.02]" : ""}`}>
              <div className="grid place-items-center h-16 w-16 rounded-2xl bg-sam-navy text-sam-amber shrink-0"><p.icon className="h-7 w-7" /></div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-sam-crimson">{p.kicker}</div>
                <h3 className="mt-1 font-display text-2xl font-extrabold text-sam-navy">{p.title}</h3>
                <p className="mt-2 text-sam-slate leading-relaxed">{p.body}</p>
                <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-1.5 text-sm font-semibold text-sam-navy"><CheckCircle2 className="h-4 w-4 text-sam-green" />{pt}</li>
                  ))}
                </ul>
              </div>
              <Button asChild className="bg-sam-crimson hover:bg-sam-crimson/90 font-semibold shrink-0" data-testid={`pillar-cta-${i + 1}`}>
                <Link to={p.to}>{p.cta}<ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Mission closer */}
      <section className="bg-sam-navy2 text-sam-cream border-t border-white/10">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black leading-tight">
            Be great. Be amazing. <span className="text-sam-crimson">Be wealthy.</span>
          </h2>
          <p className="mt-4 text-sam-cream/75 leading-relaxed max-w-2xl mx-auto">
            Whether you're a local shop owner, a home-service pro, or an ambitious agency builder, we give you the full infrastructure to win in your market and beyond.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button asChild className="bg-sam-amber text-sam-navy hover:bg-sam-amber/90 font-semibold h-11 px-5" data-testid="about-cta-list">
              <Link to="/dashboard"><MapPin className="h-4 w-4 mr-2" />List your business</Link>
            </Button>
            <Button asChild variant="outline" className="border-sam-cream/30 bg-white/5 text-sam-cream hover:bg-white/10 hover:text-sam-cream font-semibold h-11 px-5" data-testid="about-cta-coverage">
              <Link to="/coverage">See our coverage</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
